import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext, getIn } from 'formik';
import {
  fo6RoofShapesAcceptedBy360Value,
  fo6Structure360ValueUse,
  fo6StructureRoofShape,
  holdsLegacyFo6RoofShape,
  lookupsJson
} from '@ourbranch/lookups';

import Field from 'core/components/form/form.v2';
import { fo6RoofMaterialWrites, fo6RoofShapeWrites } from './FO6.utils';

const roofShapeOptions = lookupsJson.farm.barnRoofType;
const roofMaterialOptions = lookupsJson.farm.dwellingRoofType;

/**
 * #37180: an FO-6 structure's roof as two selects.
 *  - "Roof shape": building.roofShape, from farm.barnRoofType. It is the agricultural 360Value request's
 *    rooftype, so changing it re-runs 360Value (and warns, like the other valuation inputs).
 *  - "Roof material": building.roofType, from farm.dwellingRoofType, as on a dwelling and as the IMT importer
 *    writes. Neither 360Value nor ClarionDoor's structure rating reads it; it prints on the dec.
 *
 * A structure saved before this had one "Roof type" select that stored the SHAPE in roofType, and no roofShape.
 * Renewal moves it (renewalVariables.farmRenewalCorrections). Until then the shape select is bound to roofType,
 * where that shape is stored, and the material select to the blank roofShape so it shows no material. A pick
 * on either moves the roof to the new fields (fo6RoofShapeWrites / fo6RoofMaterialWrites); a structure nobody
 * touches is sent back exactly as stored. The material select is disabled until the structure has a shape (a
 * migrated structure has a material and none), so a material is never written without a shape beside it.
 *
 * #37340: 360Value rejects a shape it does not accept for the structure's use (ERR04, so no RCE). The shape
 * select offers only the shapes it accepts for the use the request sends (fo6Structure360ValueUse: an
 * open-sided barn is sent as "3"), and every shape while the use isn't known yet. A stored shape it doesn't
 * accept is flagged so staff pick another (the request leaves it out meanwhile). A poultry house is sent no shape
 * at all, but still records one from every shape (it prints on the dec): a material saved with no shape beside it
 * would read back as a roof shape stored the old way, whatever the use (adversarial review, #37340).
 */
const FO6RoofFields = ({
  fieldName,
  initialBuilding,
  disabled,
  shapeXs,
  materialXs,
  lockedShape,
  lockedShapeHelperText,
  showMaterial,
  required,
  onShapeChange
}) => {
  const { values, errors, setFieldValue } = useFormikContext();
  const building = getIn(values, `${fieldName}.building`);
  const acceptedShapes = fo6RoofShapesAcceptedBy360Value(fo6Structure360ValueUse(building));
  const sentNoShape = acceptedShapes?.length === 0;
  const storedTheOldWay = holdsLegacyFo6RoofShape(building);
  const path = (field) => `${fieldName}.building.${field}`;
  const write = (writes) => writes.forEach(({ field, value }) => setFieldValue(path(field), value));
  const shapeError = getIn(errors, path('roofShape'));
  const materialError = getIn(errors, path('roofType'));
  // 'S' is both a shape (Shed Roof) and a material (Clay/Slate): a material written on a structure with no
  // shape would read back as a Shed roof stored the old way. So a material waits for a shape (devlo, #37180).
  const currentShape = fo6StructureRoofShape(building);
  const awaitsShape = !currentShape;
  // A Quonset / hoop roof shape is fixed by the construction, so the select is locked. One that arrived without a
  // shape (migrated) keeps it blank on load; the locked select then offers just that shape, so staff can set it
  // and go on to the material (adversarial re-review, #37180).
  const offersLockedShape = Boolean(lockedShape) && awaitsShape;
  const shapeLocked = Boolean(lockedShapeHelperText) && !offersLockedShape;
  const offeredShapes = acceptedShapes?.length
    ? roofShapeOptions.filter(({ id }) => acceptedShapes.includes(id))
    : roofShapeOptions;
  const rejectedShape =
    acceptedShapes?.length && currentShape && !acceptedShapes.includes(currentShape)
      ? (roofShapeOptions.find(({ id }) => id === currentShape)?.value ?? currentShape)
      : null;
  const rejectedShapeHelperText = rejectedShape
    ? `360Value doesn't accept ${rejectedShape} for this use; pick another shape`
    : undefined;
  const notSentHelperText = sentNoShape ? 'Not sent to 360Value for this use' : undefined;

  return (
    <>
      <Field
        xs={shapeXs}
        type="select"
        id={path('roofShape')}
        name={storedTheOldWay ? path('roofType') : path('roofShape')}
        mode="light"
        label={`Roof shape${required ? '*' : ''}`}
        options={offersLockedShape ? roofShapeOptions.filter(({ id }) => id === lockedShape) : offeredShapes}
        disabled={disabled || shapeLocked}
        fast={false}
        error={!lockedShapeHelperText && (Boolean(shapeError) || Boolean(rejectedShape))}
        helperText={lockedShapeHelperText || shapeError || rejectedShapeHelperText || notSentHelperText}
        onChange={(shape) => {
          write(fo6RoofShapeWrites(building, shape));
          if (shape !== fo6StructureRoofShape(initialBuilding)) {
            onShapeChange?.();
          }
        }}
      />
      {showMaterial && (
        <Field
          xs={materialXs}
          type="select"
          id={path('roofType')}
          name={storedTheOldWay ? path('roofShape') : path('roofType')}
          mode="light"
          label={`Roof material${required ? '*' : ''}`}
          options={roofMaterialOptions}
          disabled={disabled || awaitsShape}
          fast={false}
          error={!awaitsShape && Boolean(materialError)}
          helperText={awaitsShape ? 'Pick the roof shape first' : materialError || undefined}
          onChange={(material) => write(fo6RoofMaterialWrites(building, material))}
        />
      )}
    </>
  );
};

FO6RoofFields.propTypes = {
  fieldName: PropTypes.string.isRequired,
  initialBuilding: PropTypes.object,
  disabled: PropTypes.bool,
  shapeXs: PropTypes.number,
  materialXs: PropTypes.number,
  lockedShape: PropTypes.string,
  lockedShapeHelperText: PropTypes.string,
  showMaterial: PropTypes.bool,
  required: PropTypes.bool,
  onShapeChange: PropTypes.func
};

FO6RoofFields.defaultProps = {
  initialBuilding: undefined,
  disabled: false,
  shapeXs: 4,
  materialXs: 4,
  lockedShape: undefined,
  lockedShapeHelperText: undefined,
  showMaterial: true,
  required: false,
  onShapeChange: undefined
};

export default FO6RoofFields;
